<template>
  <div class="login-page">
    <div class="login-bg">
      <span class="blob blob-a"></span>
      <span class="blob blob-b"></span>
      <span class="blob blob-c"></span>
      <span class="stars"></span>
      <span class="noise"></span>
      <span class="vignette"></span>
    </div>

    <main class="login-shell">
      <section class="login-card">
        <div class="login-grid">
          <!-- LEFT -->
          <div class="login-brand">
            <div class="brand-row">
              <span class="brand-mark" aria-hidden="true"></span>
              <div class="brand-text">
                <p class="brand-eyebrow">CEREBRO BACKOFFICE</p>
                <p class="brand-subtitle">Panel financiero unificado</p>
              </div>
            </div>

            <h1>Control total de tus finanzas.</h1>
            <p class="brand-copy">
              Visualiza inversiones, gastos y mantenimientos desde un único panel, con alertas y trazabilidad.
            </p>

            <ul class="brand-list">
              <li>
                <span class="check" aria-hidden="true">✓</span>
                KPIs claros y consistentes por periodo
              </li>
              <li>
                <span class="check" aria-hidden="true">✓</span>
                Alertas y recordatorios configurables
              </li>
              <li>
                <span class="check" aria-hidden="true">✓</span>
                Acceso seguro y SSO (Google)
              </li>
            </ul>

            <div class="brand-footer">
              <div class="quote">“Menos fricción. Más control.”</div>
              <!-- Mantengo tu asset -->
              <img class="hero-img" src="@/assets/hero-2.png" alt="Mascota Cerebro" />
            </div>
          </div>

          <!-- RIGHT -->
          <div class="login-form">
            <div class="form-header">
              <div>
                <h2>Inicia sesión</h2>
                <p class="form-subtitle">Accede a tu panel financiero con tu usuario.</p>
              </div>
              <div class="env-pill" :class="envInfo.tone">{{ envInfo.label }}</div>
            </div>

            <form class="form" @submit.prevent="handleLogin">
              <div v-if="error" class="alert">{{ error }}</div>

              <label class="field">
                Usuario
                <input
                    v-model="username"
                    type="text"
                    placeholder="usuario@dominio.com"
                    autocomplete="email"
                    inputmode="email"
                    required
                />
              </label>

              <label class="field">
                Contraseña
                <div class="password-wrap">
                  <input
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      autocomplete="current-password"
                      required
                  />
                  <button
                      type="button"
                      class="toggle-pass"
                      @click="showPassword = !showPassword"
                      :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                  >
                    {{ showPassword ? "🙈" : "👁️" }}
                  </button>
                </div>
              </label>

              <div class="form-row">
                <label class="remember">
                  <input type="checkbox" v-model="remember" />
                  Recordarme
                </label>
                <a class="forgot" href="/forgot">¿Olvidaste la contraseña?</a>
              </div>

              <button type="submit" class="primary-btn" :disabled="loading">
                {{ loading ? "Entrando…" : "Entrar" }}
              </button>

              <div class="divider">
                <span>o continúa con</span>
              </div>

              <div class="google-wrap">
                <!-- wrapper para controlar el ancho del botón Google -->
                <div class="google-btn" ref="googleButton"></div>
                <p v-if="googleError" class="error">{{ googleError }}</p>
              </div>

              <p class="legal">
                Al iniciar sesión aceptas las políticas de acceso y seguridad del sistema.
              </p>
            </form>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { login, loginWithGoogle, refreshSession } from "../services/auth";

export default {
  name: "LoginView",
  computed: {
    envInfo() {
      const raw = (process.env.VUE_APP_ENV || process.env.NODE_ENV || "development").toLowerCase();
      if (raw.includes("prod")) {
        return { label: "Producción", tone: "prod" };
      }
      if (raw.includes("stag")) {
        return { label: "Staging", tone: "stage" };
      }
      return { label: "Desarrollo", tone: "dev" };
    },
  },
  data() {
    return {
      username: "",
      password: "",
      remember: true,
      showPassword: false,
      error: "",
      loading: false,
      googleError: "",
    };
  },
  mounted() {
    this.tryRefresh();
    this.setupGoogle();
  },
  methods: {
    async tryRefresh() {
      try {
        const refreshed = await refreshSession();
        if (refreshed) {
          this.$router.push("/dashboard/overview");
        }
      } catch (_) {
        // ignore
      }
    },
    async handleLogin() {
      this.error = "";
      this.loading = true;
      try {
        await login(this.username.trim(), this.password);
        this.$router.push("/dashboard/overview");
      } catch (err) {
        this.error = err?.message || "No se pudo iniciar sesión.";
      } finally {
        this.loading = false;
      }
    },
    async setupGoogle() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        this.googleError = "Google login no configurado.";
        return;
      }
      try {
        await this.loadGoogleScript();
        if (!window.google?.accounts?.id) {
          this.googleError = "No se pudo inicializar Google.";
          return;
        }
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => this.handleGoogleCredential(response),
          auto_select: true,
          itp_support: true,
        });
        window.google.accounts.id.prompt();
        if (this.$refs.googleButton) {
          // ancho controlado por CSS del wrapper .google-btn
          window.google.accounts.id.renderButton(this.$refs.googleButton, {
            theme: "outline",
            size: "large",
            shape: "pill",
            text: "continue_with",
            width: 360,
          });
        }
      } catch (_) {
        this.googleError = "No se pudo cargar Google Sign-In.";
      }
    },
    loadGoogleScript() {
      return new Promise((resolve, reject) => {
        if (window.google?.accounts?.id) {
          resolve();
          return;
        }
        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existing) {
          existing.addEventListener("load", resolve);
          existing.addEventListener("error", reject);
          return;
        }
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async handleGoogleCredential(response) {
      if (!response?.credential) {
        this.googleError = "Credencial de Google inválida.";
        return;
      }
      this.googleError = "";
      this.loading = true;
      try {
        await loginWithGoogle(response.credential);
        this.$router.push("/dashboard/overview");
      } catch (err) {
        this.googleError = err?.message || "No se pudo iniciar sesión con Google.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ====== Base ====== */
.login-page {
  min-height: 100vh;
  background: radial-gradient(circle at 20% 20%, rgba(51, 209, 255, 0.12), transparent 45%),
    radial-gradient(circle at 80% 30%, rgba(47, 227, 138, 0.14), transparent 50%),
    #060b18;
  color: #eaf0ff;
  position: relative;
  overflow: hidden;
}

.login-page *,
.login-page *::before,
.login-page *::after {
  box-sizing: border-box;
}

.login-page input,
.login-page button,
.login-page textarea,
.login-page select {
  max-width: 100%;
}

.login-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* ====== Background FX ====== */
.blob {
  position: absolute;
  filter: blur(70px);
  opacity: 0.32;
  border-radius: 999px;
  transform: translateZ(0);
}

.blob-a {
  width: 560px;
  height: 560px;
  left: -170px;
  top: -180px;
  background: radial-gradient(circle at 30% 30%, rgba(51, 209, 255, 0.95), rgba(51, 209, 255, 0) 62%);
  animation: floatA 10s ease-in-out infinite;
}

.blob-b {
  width: 560px;
  height: 560px;
  right: -220px;
  top: 120px;
  background: radial-gradient(circle at 30% 30%, rgba(47, 227, 138, 0.85), rgba(47, 227, 138, 0) 62%);
  animation: floatB 12s ease-in-out infinite;
}

.blob-c {
  width: 440px;
  height: 440px;
  left: 28%;
  bottom: -250px;
  background: radial-gradient(circle at 30% 30%, rgba(120, 90, 255, 0.6), rgba(120, 90, 255, 0) 62%);
  animation: floatC 14s ease-in-out infinite;
}

.noise {
  position: absolute;
  inset: 0;
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='.35'/%3E%3C/svg%3E");
  background-size: 160px 160px;
}

.stars {
  position: absolute;
  inset: -10%;
  opacity: 0.35;
  background-image:
    radial-gradient(1px 1px at 12% 18%, rgba(255, 255, 255, 0.7) 0, rgba(255, 255, 255, 0) 60%),
    radial-gradient(1px 1px at 72% 22%, rgba(255, 255, 255, 0.6) 0, rgba(255, 255, 255, 0) 60%),
    radial-gradient(1.5px 1.5px at 38% 66%, rgba(255, 255, 255, 0.8) 0, rgba(255, 255, 255, 0) 60%),
    radial-gradient(1px 1px at 82% 74%, rgba(255, 255, 255, 0.65) 0, rgba(255, 255, 255, 0) 60%),
    radial-gradient(1.2px 1.2px at 20% 80%, rgba(255, 255, 255, 0.6) 0, rgba(255, 255, 255, 0) 60%);
  background-size: 240px 240px;
  filter: blur(0.2px);
  mix-blend-mode: screen;
}

/* viñeta para “premium focus” */
.vignette {
  position: absolute;
  inset: -2px;
  background: radial-gradient(60% 60% at 50% 40%, rgba(0,0,0,0) 35%, rgba(0,0,0,0.6) 100%);
  opacity: 0.9;
}

/* ====== Layout ====== */
.login-shell {
  position: relative;
  margin: 0 auto;
  max-width: 1120px;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 56px 24px;
}

.login-card {
  width: 100%;
  position: relative;
  z-index: 1;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.07),
      rgba(255, 255, 255, 0.045)
  );
  box-shadow:
      0 50px 160px rgba(0, 0, 0, 0.7),
      0 0 0 1px rgba(255, 255, 255, 0.05) inset,
      0 0 60px rgba(51, 209, 255, 0.08);
  backdrop-filter: blur(18px);
  overflow: hidden;
}

.login-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
      130deg,
      rgba(51, 209, 255, 0.7),
      rgba(255, 255, 255, 0.05),
      rgba(47, 227, 138, 0.6)
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.login-card::after {
  content: "";
  position: absolute;
  inset: -6px;
  border-radius: inherit;
  background: radial-gradient(circle at 10% 0%, rgba(51, 209, 255, 0.35), transparent 45%),
    radial-gradient(circle at 90% 0%, rgba(47, 227, 138, 0.3), transparent 45%);
  filter: blur(18px);
  opacity: 0.8;
  z-index: -1;
}

.login-grid {
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  min-width: 0;
}

.login-grid > * {
  min-width: 0;
}

/* ====== Brand column ====== */
.login-brand {
  padding: 44px 46px;
  position: relative;
}

.login-brand::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 0%, rgba(51, 209, 255, 0.12), transparent 45%),
    radial-gradient(circle at 80% 70%, rgba(47, 227, 138, 0.12), transparent 50%);
  opacity: 0.6;
  pointer-events: none;
}

.login-brand > * {
  position: relative;
  z-index: 1;
}

.brand-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 16px;
  background: linear-gradient(135deg, #33d1ff, #2fe38a);
  box-shadow:
    0 14px 40px rgba(51, 209, 255, 0.35),
    0 0 12px rgba(51, 209, 255, 0.4);
}

.brand-eyebrow {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(234, 240, 255, 0.6);
}

.brand-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: rgba(234, 240, 255, 0.8);
}

.login-brand h1 {
  margin: 30px 0 12px;
  font-size: 38px;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.brand-copy {
  margin: 0;
  color: rgba(234, 240, 255, 0.72);
  max-width: 460px;
  line-height: 1.55;
}

.brand-list {
  list-style: none;
  padding: 0;
  margin: 30px 0 0;
  display: grid;
  gap: 14px;
  color: rgba(234, 240, 255, 0.78);
  font-size: 14px;
}

.brand-list li {
  display: flex;
  align-items: center;
  gap: 12px;
}

.check {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(51, 209, 255, 0.4), rgba(255, 255, 255, 0.08));
  border: 1px solid rgba(255, 255, 255, 0.14);
  display: grid;
  place-items: center;
  font-size: 12px;
  box-shadow:
    0 0 12px rgba(51, 209, 255, 0.35),
    0 0 6px rgba(47, 227, 138, 0.25);
}

.brand-footer {
  margin-top: 36px;
  min-height: 220px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 20px;
  position: relative;
}

.quote {
  font-size: 12px;
  color: rgba(234, 240, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  padding: 11px 14px;
  border-radius: 16px;
  box-shadow: 0 0 18px rgba(255, 255, 255, 0.08);
}

.hero-img {
  width: 300px;
  height: 300px;
  object-fit: contain;
  filter:
    drop-shadow(0 18px 40px rgba(51, 209, 255, 0.35))
    drop-shadow(0 8px 20px rgba(47, 227, 138, 0.25));
  position: absolute;
  left: 50%;
  bottom: -10px;
  transform: translateX(-50%);
}

/* ====== Form column ====== */
.login-form {
  padding: 44px 46px;
  border-left: 1px solid rgba(255, 255, 255, 0.10);
  background: linear-gradient(
      180deg,
      rgba(6, 11, 24, 0.18),
      rgba(6, 11, 24, 0.04)
  );
}

.form-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.form-header h2 {
  margin: 0;
  font-size: 22px;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 6px 0 0;
  color: rgba(234, 240, 255, 0.62);
  font-size: 14px;
}

.env-pill {
  display: none;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(234, 240, 255, 0.9);
  border: 1px solid transparent;
  padding: 6px 12px;
  border-radius: 999px;
  box-shadow: 0 0 18px rgba(51, 209, 255, 0.12);
  background: rgba(255, 255, 255, 0.08);
}

.env-pill.prod {
  color: rgba(255, 220, 220, 0.95);
  background: linear-gradient(90deg, rgba(255, 90, 90, 0.22), rgba(255, 50, 80, 0.14));
  border-color: rgba(255, 90, 90, 0.35);
  box-shadow: 0 0 20px rgba(255, 80, 100, 0.25);
}

.env-pill.stage {
  color: rgba(255, 240, 210, 0.95);
  background: linear-gradient(90deg, rgba(255, 170, 70, 0.22), rgba(255, 210, 90, 0.14));
  border-color: rgba(255, 190, 90, 0.35);
  box-shadow: 0 0 20px rgba(255, 170, 70, 0.2);
}

.env-pill.dev {
  color: rgba(210, 245, 255, 0.95);
  background: linear-gradient(90deg, rgba(51, 209, 255, 0.22), rgba(47, 227, 138, 0.14));
  border-color: rgba(51, 209, 255, 0.32);
  box-shadow: 0 0 20px rgba(51, 209, 255, 0.18);
}

.form {
  margin-top: 30px;
  display: grid;
  gap: 16px;
  min-width: 0;
}

.alert {
  border: 1px solid rgba(248, 113, 113, 0.35);
  background: rgba(239, 68, 68, 0.1);
  color: #fecaca;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 13px;
}

.field {
  display: grid;
  gap: 8px;
  font-size: 13px;
  color: rgba(234, 240, 255, 0.72);
  min-width: 0;
}

.field input {
  width: 100%;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(244, 248, 255, 0.92);
  padding: 12px 14px;
  color: #1f2a3a;
  transition: border 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.field input::placeholder {
  color: rgba(31, 42, 58, 0.35);
}

.field input:focus {
  outline: none;
  border-color: rgba(51, 209, 255, 0.55);
  background: rgba(244, 248, 255, 0.98);
  box-shadow:
    0 0 0 6px rgba(51, 209, 255, 0.18),
    0 0 16px rgba(51, 209, 255, 0.2);
}

.password-wrap {
  position: relative;
  min-width: 0;
}

.toggle-pass {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 12px;
  border: 1px solid rgba(31, 42, 58, 0.12);
  background: rgba(255, 255, 255, 0.7);
  color: rgba(31, 42, 58, 0.75);
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s ease, border 0.2s ease;
}

.toggle-pass:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: rgba(31, 42, 58, 0.18);
}

.form-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: rgba(234, 240, 255, 0.72);
}

.remember {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.remember input {
  width: 16px;
  height: 16px;
  accent-color: #2fe38a;
}

.forgot {
  color: rgba(234, 240, 255, 0.72);
  text-decoration: none;
}

.forgot:hover {
  color: #eaf0ff;
}

/* CTA sólido (más premium) */
.primary-btn {
  width: 100%;
  border-radius: 18px;
  border: none;
  background: linear-gradient(90deg, rgba(51, 209, 255, 0.95), rgba(47, 227, 138, 0.95));
  color: #0b1222;
  font-weight: 800;
  padding: 12px 16px;
  cursor: pointer;
  transition: transform 0.06s ease, opacity 0.2s ease, background 0.2s ease;
  box-shadow: 0 10px 24px rgba(51, 209, 255, 0.25);
}

.primary-btn:hover {
  background: linear-gradient(90deg, rgba(51, 209, 255, 1), rgba(47, 227, 138, 1));
}

.primary-btn:active {
  transform: translateY(1px);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(234, 240, 255, 0.52);
  font-size: 11px;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.10);
}

/* Google */
.google-wrap {
  display: grid;
  gap: 12px;
  justify-items: stretch;
  min-width: 0;
}

.google-btn {
  width: 100%;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 999px;
  /* el renderButton mete su propio tamaño, esto ayuda a que quede centrado en mobile */
}

.google-btn > div,
.google-btn iframe {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.error {
  margin: 0;
  color: #fecaca;
  font-size: 13px;
}

.legal {
  font-size: 11px;
  color: rgba(234, 240, 255, 0.45);
  margin: 0;
  line-height: 1.4;
  text-align: center;
}

/* ====== Animations ====== */
@keyframes floatA {
  50% {
    transform: translate(18px, 22px) scale(1.03);
  }
}

@keyframes floatB {
  50% {
    transform: translate(-20px, -14px) scale(1.02);
  }
}

@keyframes floatC {
  50% {
    transform: translate(0, -18px) scale(1.04);
  }
}

/* ====== Responsive ====== */
@media (min-width: 900px) {
  .env-pill {
    display: inline-flex;
  }
}

@media (max-width: 960px) {
  .login-grid {
    grid-template-columns: 1fr;
  }

  .login-form {
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.10);
  }

  .hero-img {
    width: 180px;
    height: 180px;
    position: relative;
    left: auto;
    bottom: auto;
    transform: none;
    margin: 14px auto 0;
  }
}

@media (max-width: 640px) {
  .login-shell {
    padding: 34px 16px;
  }

  .login-brand,
  .login-form {
    padding: 28px 22px;
  }

  .login-brand h1 {
    font-size: 28px;
  }

  .brand-footer {
    flex-direction: column;
    align-items: center;
    min-height: 0;
    text-align: center;
  }

  .hero-img {
    width: 220px;
    height: 220px;
    margin: 12px auto 0;
  }
}
</style>
